import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const NotificationSettingsContext = createContext(null);

// Wartości domyślne - wszystkie typy włączone dla wszystkich opcji
const DEFAULT_SETTINGS = {
  newEventNearby: { inApp: true, email: true, push: true },
  friendInvitation: { inApp: true, email: true, push: true },
  friendJoinedEvent: { inApp: true, email: true, push: true },
  eventStartsInHour: { inApp: true, email: true, push: true },
  applicationAccepted: { inApp: true, email: true, push: true },
  eventCreated: { inApp: true, email: true, push: true },
  newApplicationToEvent: { inApp: true, email: true, push: true },
};

// Mapowanie kluczy z komponentu na nazwy kolumn w bazie
const KEY_TO_COLUMN = {
  newEventNearby: 'new_event_nearby',
  friendInvitation: 'friend_invitation',
  friendJoinedEvent: 'friend_joined_event',
  eventStartsInHour: 'event_starts_in_hour',
  applicationAccepted: 'application_accepted',
  eventCreated: 'event_created',
  newApplicationToEvent: 'new_application_to_event',
};

export function NotificationSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Funkcja do konwersji danych z bazy na format komponentu
  const normalizeSettings = useCallback((dbSettings) => {
    if (!dbSettings) return DEFAULT_SETTINGS;

    return {
      newEventNearby: dbSettings.new_event_nearby || DEFAULT_SETTINGS.newEventNearby,
      friendInvitation: dbSettings.friend_invitation || DEFAULT_SETTINGS.friendInvitation,
      friendJoinedEvent: dbSettings.friend_joined_event || DEFAULT_SETTINGS.friendJoinedEvent,
      eventStartsInHour: dbSettings.event_starts_in_hour || DEFAULT_SETTINGS.eventStartsInHour,
      applicationAccepted: dbSettings.application_accepted || DEFAULT_SETTINGS.applicationAccepted,
      eventCreated: dbSettings.event_created || DEFAULT_SETTINGS.eventCreated,
      newApplicationToEvent: dbSettings.new_application_to_event || DEFAULT_SETTINGS.newApplicationToEvent,
    };
  }, []);

  // Funkcja do konwersji danych z komponentu na format bazy
  const denormalizeSettings = useCallback((componentSettings) => {
    return {
      new_event_nearby: componentSettings.newEventNearby,
      friend_invitation: componentSettings.friendInvitation,
      friend_joined_event: componentSettings.friendJoinedEvent,
      event_starts_in_hour: componentSettings.eventStartsInHour,
      application_accepted: componentSettings.applicationAccepted,
      event_created: componentSettings.eventCreated,
      new_application_to_event: componentSettings.newApplicationToEvent,
    };
  }, []);

  // Pobierz ustawienia z bazy danych
  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // Brak użytkownika - ustaw domyślne wartości
        setSettings(DEFAULT_SETTINGS);
        setUserId(null);
        setIsLoading(false);
        return;
      }

      setUserId(user.id);

      // Pobierz ustawienia z bazy
      const { data: dbSettings, error: fetchError } = await supabase
        .from('user_notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!dbSettings) {
        // Brak rekordu dla użytkownika - użyj domyślnych (bez błędu 406 w konsoli)
        setSettings(DEFAULT_SETTINGS);
        setIsLoading(false);
        return;
      }

      // Normalizuj dane z bazy do formatu komponentu
      const normalized = normalizeSettings(dbSettings);
      setSettings(normalized);
    } catch (err) {
      console.error('Error fetching notification settings:', err);
      setError(err.message);
      // W przypadku błędu, ustaw domyślne wartości (fail-safe)
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, [normalizeSettings]);

  // Aktualizuj pojedyncze ustawienie
  const updateNotificationSetting = useCallback(async (notificationKey, type, value) => {
    if (!KEY_TO_COLUMN[notificationKey]) {
      throw new Error(`Unknown notification key: ${notificationKey}`);
    }

    const columnName = KEY_TO_COLUMN[notificationKey];
    
    // Optymistyczna aktualizacja UI
    setSettings(prev => ({
      ...prev,
      [notificationKey]: {
        ...prev[notificationKey],
        [type]: value
      }
    }));

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not logged in');
      }

      // Pobierz aktualne ustawienie dla tego powiadomienia
      const currentSetting = settings[notificationKey];
      const updatedSetting = {
        ...currentSetting,
        [type]: value
      };

      // Sprawdź czy wpis istnieje w bazie
      const { data: existing } = await supabase
        .from('user_notification_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing?.id) {
        // Aktualizuj istniejący wpis
        const { error: updateError } = await supabase
          .from('user_notification_settings')
          .update({ [columnName]: updatedSetting })
          .eq('user_id', user.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Utwórz nowy wpis z domyślnymi wartościami i zaktualizowanym ustawieniem
        const newSettings = { ...DEFAULT_SETTINGS };
        newSettings[notificationKey] = updatedSetting;
        
        const { error: insertError } = await supabase
          .from('user_notification_settings')
          .insert({
            user_id: user.id,
            ...denormalizeSettings(newSettings)
          });

        if (insertError) {
          // Równoległy zapis mógł już utworzyć rekord - spróbuj wtedy update.
          if (insertError.code === '23505') {
            const { error: retryUpdateError } = await supabase
              .from('user_notification_settings')
              .update({ [columnName]: updatedSetting })
              .eq('user_id', user.id);

            if (retryUpdateError) {
              throw retryUpdateError;
            }
          } else {
            throw insertError;
          }
        }
      }

      // Nie wywołujemy fetchSettings() - optymistyczna aktualizacja już zaktualizowała stan
      // fetchSettings() powodowałoby re-render i scrollowanie na górę
      
      // Wyślij wiadomość do innych okien przez BroadcastChannel
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('notification_settings_sync');
        channel.postMessage({ 
          type: 'settings_updated', 
          userId: user.id,
          notificationKey,
          settingType: type,
          value 
        });
        channel.close();
      }
    } catch (err) {
      console.error('Error updating notification setting:', err);
      setError(err.message);
      // Przywróć poprzednią wartość w przypadku błędu
      await fetchSettings();
      throw err;
    }
  }, [settings, fetchSettings, denormalizeSettings]);

  // Przełącz powiadomienie ON/OFF (wszystkie typy)
  const toggleNotification = useCallback(async (notificationKey, isOn) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not logged in');
      }

      const columnName = KEY_TO_COLUMN[notificationKey];
      if (!columnName) {
        throw new Error(`Unknown notification key: ${notificationKey}`);
      }

      let updatedSetting;
      if (isOn) {
        // Włącz - domyślnie tylko inApp
        updatedSetting = { inApp: true, email: false, push: false };
      } else {
        // Wyłącz wszystkie typy
        updatedSetting = { inApp: false, email: false, push: false };
      }

      // Optymistyczna aktualizacja UI
      setSettings(prev => ({
        ...prev,
        [notificationKey]: updatedSetting
      }));

      // Sprawdź czy wpis istnieje w bazie
      const { data: existing } = await supabase
        .from('user_notification_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing?.id) {
        // Aktualizuj istniejący wpis
        const { error: updateError } = await supabase
          .from('user_notification_settings')
          .update({ [columnName]: updatedSetting })
          .eq('user_id', user.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Utwórz nowy wpis z domyślnymi wartościami i zaktualizowanym ustawieniem
        const newSettings = { ...DEFAULT_SETTINGS };
        newSettings[notificationKey] = updatedSetting;
        
        const { error: insertError } = await supabase
          .from('user_notification_settings')
          .insert({
            user_id: user.id,
            ...denormalizeSettings(newSettings)
          });

        if (insertError) {
          // Równoległy zapis mógł już utworzyć rekord - spróbuj wtedy update.
          if (insertError.code === '23505') {
            const { error: retryUpdateError } = await supabase
              .from('user_notification_settings')
              .update({ [columnName]: updatedSetting })
              .eq('user_id', user.id);

            if (retryUpdateError) {
              throw retryUpdateError;
            }
          } else {
            throw insertError;
          }
        }
      }

      // Nie wywołujemy fetchSettings() - optymistyczna aktualizacja już zaktualizowała stan
      // fetchSettings() powodowałoby re-render i scrollowanie na górę
      
      // Wyślij wiadomość do innych okien przez BroadcastChannel
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('notification_settings_sync');
        channel.postMessage({ 
          type: 'settings_updated', 
          userId: user.id,
          notificationKey,
          isOn 
        });
        channel.close();
      }
    } catch (err) {
      console.error('Error toggling notification:', err);
      setError(err.message);
      // Przywróć poprzednią wartość w przypadku błędu
      await fetchSettings();
      throw err;
    }
  }, [fetchSettings, denormalizeSettings]);

  useEffect(() => {
    fetchSettings();

    // BroadcastChannel - synchronizacja między oknami tej samej przeglądarki
    let broadcastChannel = null;
    if (typeof BroadcastChannel !== 'undefined' && userId) {
      broadcastChannel = new BroadcastChannel('notification_settings_sync');
      
      broadcastChannel.onmessage = (event) => {
        const { type, userId: messageUserId, notificationKey, settingType, value, isOn } = event.data;
        
        // Ignoruj wiadomości z innych użytkowników
        if (type === 'settings_updated' && messageUserId === userId) {
          // Inne okno zmieniło ustawienia - zaktualizuj cache bezpośrednio (bez fetchSettings)
          if (notificationKey && settingType !== undefined && value !== undefined) {
            // Aktualizacja pojedynczego typu powiadomienia
            setSettings(prev => ({
              ...prev,
              [notificationKey]: {
                ...prev[notificationKey],
                [settingType]: value
              }
            }));
          } else if (notificationKey && isOn !== undefined) {
            // Przełączenie całego powiadomienia ON/OFF
            const updatedSetting = isOn 
              ? { inApp: true, email: false, push: false }
              : { inApp: false, email: false, push: false };
            setSettings(prev => ({
              ...prev,
              [notificationKey]: updatedSetting
            }));
          } else {
            // Fallback - jeśli nie mamy szczegółów, odśwież wszystko (ale to nie powinno się zdarzyć)
            fetchSettings();
          }
        }
      };
    }

    // Nasłuchuj zmian w autoryzacji (np. wylogowanie, ponowne zalogowanie)
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id !== userId) {
        // Tylko jeśli zmienił się użytkownik
        fetchSettings();
      } else if (!session) {
        // Wylogowanie - wyczyść cache
        setSettings(DEFAULT_SETTINGS);
        setUserId(null);
        setIsLoading(false);
      }
    });

    return () => {
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      authListener.subscription.unsubscribe();
    };
  }, [fetchSettings, userId]);

  const value = {
    settings,
    isLoading,
    error,
    updateNotificationSetting,
    toggleNotification,
    refetch: fetchSettings,
  };

  return (
    <NotificationSettingsContext.Provider value={value}>
      {children}
    </NotificationSettingsContext.Provider>
  );
}

export function useNotificationSettingsContext() {
  const context = useContext(NotificationSettingsContext);
  if (!context) {
    throw new Error('useNotificationSettingsContext must be used within NotificationSettingsProvider');
  }
  return context;
}

